/**
 * @fileoverview `@puffin-core/error-handling/failure-words` — the ONE home of the words a failure's CLASS earns on any
 *   screen, before (or instead of) the screen's own words.
 * @package @puffin-core/error-handling
 *
 * Two answers, each a `{ key, ns }` pair to say with `t(words.key, { ns: words.ns })`, or `null` when the failure is
 * its caller's to word:
 *
 * - {@link unreachedFailureWords}: a request no server answered. Its class (`failureClassOf`: a network failure or a
 *   timeout) is the one fact a reader can act on ("check your connection", "took too long"), so it keeps that class's
 *   display words in namespace #57, the same copy `formatApiErrorForDisplay` gives it.
 * - {@link accessRefusalWords}: a gate refused the person a permission. Judged by the refusal's CODE, never by the 403
 *   alone: a 403 also carries a step-up demand (`MFA_REQUIRED`), a scope a route requires (`PLATFORM_SCOPE_REQUIRED`,
 *   `TENANT_WIDE_SCOPE_REQUIRED`) or a feature's own refusal (`LOCATION_FORBIDDEN`), and telling that reader "you do not
 *   have permission" would be false. It reads the `forbidden` display words.
 *
 * Every caller asks in ONE order: unreached → its own feature refusals → access → its operation's own words. A
 * feature that knows a code words it first, so the generic access words never cover a refusal a screen can say better.
 * {@link defineFailureWords} writes that order ONCE: a screen's module declares its words and gets the function.
 *
 * ## Why one home (ErrorState Unit B, THINKPAD's P1 ruling)
 *
 * Four apps (console, planner-web, palm-native, payday-web) each carried a byte-identical `core/api/unreachedFailureWords`
 * and page-shell's `<ErrorState>` a fifth copy of the same branch: the drift class itself. They read this module now.
 *
 * ## ESM-only, and never the barrel
 *
 * Words carry no CommonJS half (the `./strings` precedent: a server reads a code, never a sentence), so the
 * `./failure-words` subpath has no `require` condition. This module imports the handler and the zero-import namespace
 * leaf directly, never the package barrel, so a leaf that wants the words does not take the whole package.
 *
 * @module @puffin-core/error-handling/failure-words
 */

import { API_ERROR_CODES } from './constants/errorConstants.js';
import { readErrorCode } from './errorCode.js';
import {
  createApiError,
  failureClassOf,
  formatApiErrorForDisplay,
} from './handlers/apiErrorHandlers.js';
import { ERROR_HANDLING_NAMESPACE } from './strings/namespace.js';

/**
 * Failure words: a catalog key, with its namespace when it is not the screen's own and its English default when the
 * screen's catalog carries one. Say them with {@link sayFailureWords}.
 *
 * @typedef {Readonly<{key: string, ns?: string, defaultValue?: string}>} FailureWords
 */

/**
 * A class's display words, keyed by the formatter from a representative error of that class — never typed here.
 *
 * @param {Object} representative - `createApiError` facts of one error of the class
 * @returns {Readonly<{key: string, ns: string}>}
 */
const classWords = (representative) =>
  Object.freeze({
    key: formatApiErrorForDisplay(createApiError({ message: '', ...representative })).messageKey,
    ns: ERROR_HANDLING_NAMESPACE,
  });

/** The words of each class in which no server answered. */
export const UNREACHED_FAILURE_WORDS = Object.freeze({
  [API_ERROR_CODES.NETWORK_ERROR]: classWords({ code: 'ERR_NETWORK' }),
  [API_ERROR_CODES.TIMEOUT]: classWords({ code: 'ECONNABORTED' }),
});

/**
 * The codes a gate refuses a missing permission with: `PERMISSION_DENIED`, the ONE refusal every matrix gate answers,
 * tenant and platform (`@puffin-core/middleware` `gateRefusals.refuseMissingPermission`; the platform gate's
 * `INSUFFICIENT_PERMISSION` converged onto it in PERMISSIONS-CANON-001), and a rank floor's generic `FORBIDDEN`.
 */
export const ACCESS_REFUSAL_CODES = Object.freeze(['PERMISSION_DENIED', 'FORBIDDEN']);

/** The words a refused permission reads as. */
const ACCESS_REFUSAL_WORDS = classWords({ status: 403 });

/**
 * The words for a request no server answered, or `null` when a server answered (or there was no request).
 *
 * @param {*} error - What the request rejected with
 * @returns {?Readonly<{key: string, ns: string}>}
 */
export function unreachedFailureWords(error) {
  const failureClass = failureClassOf(error);
  return Object.hasOwn(UNREACHED_FAILURE_WORDS, failureClass)
    ? UNREACHED_FAILURE_WORDS[failureClass]
    : null;
}

/**
 * The words for a gate's refusal of a permission, or `null` for any other failure.
 *
 * @param {*} error - What the request rejected with (its code is read through its `cause` chain)
 * @returns {?Readonly<{key: string, ns: string}>}
 */
export function accessRefusalWords(error) {
  return ACCESS_REFUSAL_CODES.includes(readErrorCode(error)) ? ACCESS_REFUSAL_WORDS : null;
}

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Refuse a descriptor map whose entries are not words, naming the module and the entry.
 *
 * @param {string} module
 * @param {string} path - the map's name in the module's options, for the message
 * @param {Object} map
 * @returns {void}
 * @throws {TypeError}
 */
function assertDescriptors(module, path, map) {
  for (const [name, descriptor] of Object.entries(map)) {
    if (typeof descriptor?.key !== 'string') {
      throw new TypeError(`${module}: ${path}.${name} has no string key`);
    }
  }
}

/**
 * Check a module's options once, at definition: a wrong definition is a programmer error and never reaches a screen.
 *
 * @param {Object} options - `defineFailureWords`' options
 * @returns {void}
 * @throws {TypeError}
 */
function assertDefinition({ module, refusals, operationRefusals, operations }) {
  if (typeof module !== 'string' || module === '') {
    throw new TypeError('defineFailureWords: a module name is required');
  }
  if (!isPlainObject(operations) || Object.keys(operations).length === 0) {
    throw new TypeError(`${module}: operations must name at least one operation`);
  }
  if (!isPlainObject(refusals)) throw new TypeError(`${module}: refusals must be an object`);
  if (!isPlainObject(operationRefusals)) {
    throw new TypeError(`${module}: operationRefusals must be an object`);
  }
  assertDescriptors(module, 'operations', operations);
  assertDescriptors(module, 'refusals', refusals);
  for (const [operation, map] of Object.entries(operationRefusals)) {
    if (!Object.hasOwn(operations, operation)) {
      throw new TypeError(`${module}: operationRefusals names "${operation}", which is not an operation`);
    }
    if (!isPlainObject(map)) {
      throw new TypeError(`${module}: operationRefusals.${operation} must be an object`);
    }
    assertDescriptors(module, `operationRefusals.${operation}`, map);
  }
}

/**
 * Define a module's failure words: ONE function that says a failed operation in the ruled order.
 *
 * 1. a request no server answered → its class's words ({@link unreachedFailureWords});
 * 2. a refusal the operation names (`operationRefusals[operation][code]`), then one the module names
 *    (`refusals[code]`) → the module's own words;
 * 3. a gate's permission refusal the module does not name → the access words ({@link accessRefusalWords});
 * 4. anything else → the operation's own words.
 *
 * Every words object is the module's own descriptor (`{ key, ns?, defaultValue? }`) or the class words, handed back
 * unchanged: the factory writes no copy. The code is read through the error's `cause` chain (`readErrorCode`), and only
 * an OWN property of a map counts, so `constructor` is never a refusal.
 *
 * @param {Object} options
 * @param {string} options.module - the module's name, for every programmer-error message
 * @param {Object<string, {key: string}>} options.operations - each operation's own words
 * @param {Object<string, {key: string}>} [options.refusals] - words for a code, whichever operation met it
 * @param {Object<string, Object<string, {key: string}>>} [options.operationRefusals] - words for a code one operation
 *   meets in its own sense (a list's `VALIDATION_ERROR` is a bad filter; a read's, a bad reference)
 * @returns {(error: *, operation: string) => FailureWords}
 * @throws {TypeError} at definition for options that are not words
 */
export function defineFailureWords({
  module,
  refusals = {},
  operationRefusals = {},
  operations,
} = {}) {
  assertDefinition({ module, refusals, operationRefusals, operations });
  return function failureWords(error, operation) {
    if (!Object.hasOwn(operations, operation)) {
      throw new TypeError(`${module}: unknown operation "${operation}"`);
    }
    const unreached = unreachedFailureWords(error);
    if (unreached) return unreached;
    const code = readErrorCode(error);
    const ownRefusals = operationRefusals[operation];
    if (code !== null && ownRefusals && Object.hasOwn(ownRefusals, code)) return ownRefusals[code];
    if (code !== null && Object.hasOwn(refusals, code)) return refusals[code];
    return accessRefusalWords(error) ?? operations[operation];
  };
}

/**
 * Say failure words in the reader's language: the ONE spelling of `t(key, { ns, defaultValue })` for a descriptor
 * from this module, so no screen re-writes which fields a descriptor carries.
 *
 * @param {Function} t - the screen's translate function, called as `t(key, { ns, defaultValue })`
 * @param {FailureWords} words
 * @returns {string}
 */
export function sayFailureWords(t, words) {
  return t(words.key, { ns: words.ns, defaultValue: words.defaultValue });
}
